import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Users } from 'lucide-react';

const AllUsersCard = ({
  users,
  usersLoading,
  usersTotal,
  currentPage,
  pageSize,
  onLoadUsers,
  onToggleTier,
  toggleLoading,
}) => (
  <Card className="bg-gray-800 border-gray-700">
    <CardHeader>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            All Users
          </CardTitle>
          <CardDescription className="text-gray-400">View and manage all registered users</CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onLoadUsers(0)}
          disabled={usersLoading}
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
          data-testid="admin-load-users-btn"
        >
          {usersLoading ? 'Loading...' : 'Load Users'}
        </Button>
      </div>
    </CardHeader>
    {users.length > 0 && (
      <CardContent>
        <div className="rounded-lg border border-gray-700 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700 hover:bg-gray-700/50">
                <TableHead className="text-gray-400">User</TableHead>
                <TableHead className="text-gray-400">Tier</TableHead>
                <TableHead className="text-gray-400">Plan</TableHead>
                <TableHead className="text-gray-400">Created</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-gray-700 hover:bg-gray-700/50">
                  <TableCell>
                    <div>
                      <p className="font-medium text-white">{u.name || '-'}</p>
                      <p className="text-sm text-gray-400">{u.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={u.tier === 'pro' ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-300'}
                    >
                      {u.tier === 'pro' ? 'Pro' : 'Free'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-300">{u.plan || '-'}</TableCell>
                  <TableCell className="text-gray-400 text-sm">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onToggleTier(u.email, u.tier)}
                      disabled={toggleLoading === u.email}
                      className={
                        u.tier === 'pro'
                          ? 'border-red-600 text-red-400 hover:bg-red-600 hover:text-white'
                          : 'border-green-600 text-green-400 hover:bg-green-600 hover:text-white'
                      }
                    >
                      {toggleLoading === u.email ? '...' : u.tier === 'pro' ? 'Remove Pro' : 'Make Pro'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-400">
            Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, usersTotal)} of {usersTotal}{' '}
            users
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onLoadUsers(currentPage - 1)}
              disabled={currentPage === 0 || usersLoading}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onLoadUsers(currentPage + 1)}
              disabled={(currentPage + 1) * pageSize >= usersTotal || usersLoading}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    )}
  </Card>
);

export default AllUsersCard;
